import {
  FIELDS,
  GeneralSection,
  MortgagePropertySection,
  LoanSection,
  BorrowerSection,
} from 'modules/cm/domain/PepperUploadPreview/PepperUploadPreview';
import { Rows as ViewModelRows, Cell } from 'modules/cm/components/UWTable/UWTable.types';
import { Nullable } from 'utils/types';
import { GeneralFields, MortgagePropertyFields, LoanFields, BorrowerFields } from 'modules/cm/form/PepperReview/fields';

export type Labelled = { mapper: (value: any) => string; label: string };

type Rows<F> = Array<
  Omit<ViewModelRows[0], 'htmlClass' | 'cells'> & {
    htmlClass?: ViewModelRows[0]['htmlClass'];
    cells?: ViewModelRows[0]['cells'];
    field: F;
  }
>;

const createCell = (field: Labelled, value: any): Cell => {
  return {
    htmlClass: 'bold',
    value: field.mapper(value),
  };
};

export const mapToViewModelRows = function<T = Array<object>>(
  rows: Rows<keyof T>,
  data: Nullable<T>[],
  fields: { [key in keyof T]: Labelled },
): ViewModelRows {
  return rows.map(({ htmlClass = null, cells = [], field }) => {
    const prop = field as keyof T;
    const singleField = fields[prop];
    const label = singleField.label;
    const cellsWithValue = data.filter((_) => _).map((field) => createCell(singleField, (field as T)[prop]));

    return {
      htmlClass,
      cells: [
        { htmlClass: 'normal', value: label },
        ...cellsWithValue.map((cell) => {
          return { htmlClass: 'bold', value: cell.value };
        }),
      ],
    };
  });
};

export const mapGeneralToViewModel = (data: GeneralSection): ViewModelRows => {
  return mapToViewModelRows<GeneralSection>(
    [
      { field: FIELDS.CASE_ID },
      { field: FIELDS.ORIGINATION_ID },
      { field: FIELDS.NUMBER_OF_APPLICANTS },
      { field: FIELDS.ACCOUNT_NUMBER },
      { field: FIELDS.SORT_CODE },
    ],
    [data],
    GeneralFields,
  );
};

export const mapMortgagePropertyToViewModel = (data: MortgagePropertySection): ViewModelRows => {
  return mapToViewModelRows<MortgagePropertySection>(
    [
      { field: FIELDS.TITLE_NUMBER },
      { field: FIELDS.FLAT_NUMBER },
      { field: FIELDS.BUILDING_NAME },
      { field: FIELDS.BUILDING_NUMBER },
      { field: FIELDS.STREET },
      { field: FIELDS.CITY },
      { field: FIELDS.PROPERTY_POST_CODE },
      { field: FIELDS.PROPERTY_TYPE },
      { field: FIELDS.TENURE },
      { field: FIELDS.PROPERTY_BUILT_YEAR },
      { field: FIELDS.ACTUAL_PROPERTY_VALUE },
      { field: FIELDS.INSPECTION_DATE },
    ],
    [data],
    MortgagePropertyFields,
  );
};

export const mapLoanToViewModel = (data: LoanSection): ViewModelRows => {
  return mapToViewModelRows<LoanSection>(
    [
      { field: FIELDS.PEPPER_PURPOSE_OF_THE_LOAN },
      { field: FIELDS.FCA_LOAN_PURPOSE },
      { field: FIELDS.NET_LOAN },
      { field: FIELDS.IS_BUY_TO_LET },
      { field: FIELDS.IS_MARGIN_ACTIVE },
      { field: FIELDS.IS_APP_ACTIVE },
      { field: FIELDS.PRODUCT_FEE },
      { field: FIELDS.PRODUCT_FEE_PAYMENT },
      { field: FIELDS.COMPLETION_DATE },
      { field: FIELDS.VARIABLE_TERM_START_DATE },
      { field: FIELDS.NUMBER_OF_PAYMENTS },
      { field: FIELDS.FIXED_INTEREST_RATE },
      { field: FIELDS.REVERT_MARGIN },
      { field: FIELDS.LVR },
    ],
    [data],
    LoanFields,
  );
};

export const mapBorrower = (data: BorrowerSection): ViewModelRows => {
  return mapToViewModelRows<BorrowerSection[FIELDS.APPLICANT_1]>(
    [
      { field: FIELDS.BORROWER_TITLE },
      { field: FIELDS.BORROWER_LAST_NAME },
      { field: FIELDS.BORROWER_FIRST_NAME },
      { field: FIELDS.BORROWER_GENDER },
      { field: FIELDS.BORROWER_DATE_OF_BIRTH },
      { field: FIELDS.BORROWER_PHONE_HOME },
      { field: FIELDS.BORROWER_PHONE_MOBILE },
      { field: FIELDS.BORROWER_PHONE_WORK },
      { field: FIELDS.BORROWER_EMAIL },
      { field: FIELDS.BORROWER_ADDITIONAL_EMAIL },
      { field: FIELDS.BORROWER_EMPLOYMENT_STATUS },
      { field: FIELDS.BORROWER_TOTAL_CURRENT_INCOME },
    ],
    [data[FIELDS.APPLICANT_1], data[FIELDS.APPLICANT_2]],
    BorrowerFields[FIELDS.APPLICANT_1],
  );
};
